import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSocket } from '../utils/socket';
import { getAuthHeader } from '../utils/authUtils';

const API = process.env.REACT_APP_API_URL;

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Bell in the top bar: the same alerts that were pushed to this user's
 * phone, newest first, with an unread badge. Live-updated over the socket.
 */
function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/notifications`, { params: { limit: 40 }, headers: getAuthHeader() });
      setItems(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // keep whatever is shown; the next refresh will retry
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, [load]);

  // The socket may connect after mount; keep trying until it is there
  useEffect(() => {
    let socket = null;
    const onNotification = (doc) => {
      setItems((prev) => [doc, ...prev.filter((n) => n._id !== doc._id)].slice(0, 40));
      setUnreadCount((n) => n + 1);
    };
    const attach = () => {
      const s = getSocket();
      if (!s || s === socket) return;
      socket = s;
      socket.on('notification', onNotification);
    };
    attach();
    const retry = setInterval(attach, 5000);
    return () => {
      clearInterval(retry);
      if (socket) socket.off('notification', onNotification);
    };
  }, []);

  const open = (event) => {
    setAnchorEl(event.currentTarget);
    load();
  };
  const close = () => setAnchorEl(null);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/read-all`, {}, { headers: getAuthHeader() });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/api/notifications`, { headers: getAuthHeader() });
      setItems([]);
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const openItem = async (item) => {
    close();
    if (!item.read) {
      setItems((prev) => prev.map((n) => (n._id === item._id ? { ...n, read: true } : n)));
      setUnreadCount((n) => Math.max(0, n - 1));
      axios.put(`${API}/api/notifications/${item._id}/read`, {}, { headers: getAuthHeader() }).catch(() => {});
    }
    navigate(item.url || (item.po ? `/dashboard?po=${encodeURIComponent(item.po)}` : '/dashboard'));
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" size="large" onClick={open} aria-label="notifications">
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '95vw' } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notifications</Typography>
          <Box>
            <Button size="small" onClick={markAllRead} disabled={unreadCount === 0}>Mark all read</Button>
            <Button size="small" color="inherit" onClick={clearAll} disabled={items.length === 0}>Clear</Button>
          </Box>
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {loading ? 'Loading…' : 'Nothing yet. Alerts sent to your phone will show up here.'}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 480, overflowY: 'auto' }}>
            {items.map((item) => (
              <ListItemButton
                key={item._id}
                onClick={() => openItem(item)}
                sx={{ alignItems: 'flex-start', bgcolor: item.read ? 'transparent' : 'action.hover' }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 1, mr: 1.5, flexShrink: 0, bgcolor: item.read ? 'transparent' : 'primary.main' }} />
                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      {item.body}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {item.po ? `PO ${item.po} · ` : ''}{timeAgo(item.createdAt)}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{ fontWeight: item.read ? 400 : 600, fontSize: 14 }}
                  secondaryTypographyProps={{ component: 'div', fontSize: 13 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}

export default NotificationBell;
